import React, { useState, useRef, useEffect } from 'react';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import axios, { CancelTokenSource } from 'axios';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { handleSaveErrors } from 'services/api/handleSaveErrors';
import { sanitizeTextareaFromString } from 'services/utils/convertTextareaData';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import CustomQuillEditor, { CustomQuillEditorHandle } from 'components/forms/fields/CustomQuillEditor';
import { convertTabsToSpaces } from 'services/utils/convertTextareaData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { TimelineItemType } from 'types/TimelineTypes';
import 'react-quill/dist/quill.snow.css';
import '../../style/scss/timeline.scss';

interface TimelineNoteFormProps {
    viewKey?: string;
    placement: 'inline' | 'modal';
    timeline_item_id?: number;
    noteId?: number;
    timeline_page: string;  // The object name of the timeline page, for example: 'organization'
    timeline_page_id: number;  // The id of the timeline page, for example organization '1'
    closeForm?: () => void;
    onSubmitSuccess?: (newItem: TimelineItemType) => void;
}

const TimelineNoteForm: React.FC<TimelineNoteFormProps> = ({ 
    viewKey, placement, timeline_item_id, noteId, timeline_page_id, timeline_page, 
    closeForm, onSubmitSuccess
}) => {
    const { t } = useTranslation();
    const { errorMessages, setErrorMessages, setFloatingAlert, setUnsavedChanges, unsavedChangesMap } = useGlobalContext();
    const { revealModal, closeModal } = useModal();
    const { handleLogout, userHash } = useAuthContext();
    const [originalNoteText, setOriginalNoteText] = useState('');
    const [noteText, setNoteText] = useState('');
    const [buttonLoader, setButtonLoader] = useState(false);
    const [, setShowErrorAlert] = useState(false);
    const [cancelSource, setCancelSource] = useState<CancelTokenSource | null>(null);
    const [loadStatus, setLoadStatus] = useState<"idle" | "loading" | "loaded">("idle");
    const [showLoader, setShowLoader] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const showInModal = placement === 'modal';
    const quillRef = useRef<CustomQuillEditorHandle>(null);
    const fieldNames = ['note'];

    // If editing an existing note, fetch the existing data
    useEffect(() => {
        // Set load status to loading
        setLoadStatus("loading");

        // Show loader icon after 250ms
        let timer: NodeJS.Timeout | null = setTimeout(() => {
            setShowLoader(true);
        }, 250);

        const loadNoteData = async () => {
            if (noteId) {
                try {
                    const data = await fetchData({ apiObject: 'note', itemId: noteId, handleLogout });
                    if (data) {
                        // Convert tabs to non-breakable spaces to show the tabs in the frontend
                        const convertText = convertTabsToSpaces(data.note)
                        setNoteText(convertText);
                        setOriginalNoteText(convertText);

                        if (showInModal) {
                            revealModal();
                        }
                    }
                } catch (error) {
                    console.log('Failed to load note data', error);
                }
            } else {
                setNoteText('');
                setOriginalNoteText('');

                if (showInModal) {
                    revealModal();
                }
            }
        };

        // Start both async operations
        const loadPromise = [loadNoteData()];

        // Set load status to loaded on completion
        Promise.all(loadPromise).then(() => {
            setLoadStatus("loaded");
            if (timer) {clearTimeout(timer)};
            setShowLoader(false);
        });
    }, [noteId]);

    // Set unsaved changes when note data changes
    useEffect(() => {
        // Helper function to strip the html from the text
        const stripHtml = (html: string) => html
            .replace(/<br\s*\/?>/gi, '')
            .replace(/<\/?[^>]+(>|$)/g, '')
            .replace(/\u00A0/g, ' ')
            .trim();

        // Check if the note is changed
        const hasChanges = stripHtml(noteText) !== stripHtml(originalNoteText);

        // Set unsaved changes
        if (showInModal && viewKey) {
            setUnsavedChanges(viewKey, hasChanges)
        }
    }, [noteText, originalNoteText]);

    // Handle delete
    const handleDelete = async () => {
        if (noteId) {
            try {
                // Call the delete note function on the backend
                const response = await saveData({ 
                    apiUrl: `delete_note/${noteId}`,
                    method: 'patch',
                    data: {},
                    source: axios.CancelToken.source()
                })

                // Handle successful response
                if (response && response.status === 200) {
                    // Show success alert
                    setFloatingAlert({ 'type': 'success' });

                    // Update the timeline items state
                    onSubmitSuccess?.({
                        ...response.data,
                        timeline_object_type: 'note'
                    });

                    closeForm?.();
        
                    if (showInModal) {
                        closeModal();
                    }
                }
            } catch (error) {
                setFloatingAlert({ type: 'danger', message: 'timeline.note.alert.delete_note_failed_message' })
            }
        }
    }

    // Handles the submit of the note
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setButtonLoader(true);

            // Create the cancel token source
            const source = axios.CancelToken.source();
            setCancelSource(source);

            // Sanitize textarea before submit
            const sanitizedNote = sanitizeTextareaFromString(noteText);

            // Set the fields to save the note
            const submitData = {
                note: sanitizedNote,
                [timeline_page as string]: timeline_page_id,
                ...(noteId ? { 
                    modified_by_user_hash: userHash,
                    timeline_item: timeline_item_id
                } : { 
                    created_by_user_hash: userHash 
                }),
            }

            // Configure the save data props
            const apiUrl = noteId ? `patch_note/${noteId}` : 'post_note';
            const method = noteId ? 'patch' : 'post';

            // Save the note in the backend
            const response = await saveData({ apiUrl, method, data: submitData, source });

            // Handle successful response
            if (response && (response.status === 200 || response.status === 201)) {
                // Show success alert
                setFloatingAlert({ 'type': 'success' });

                setButtonLoader(false);
                setErrorMessages({});

                // Update the timeline items state
                onSubmitSuccess?.({
                    ...response.data,
                    timeline_object_type: 'note'
                });

                if (viewKey) {
                    setUnsavedChanges(viewKey, false);
                }

                closeForm?.();

                if (showInModal) {
                    closeModal();
                }
            }
        } catch (error) {
            // Handle the errors using the handleSaveErrors function
            const errorData = handleSaveErrors(error, fieldNames); 
            setErrorMessages(errorData);

            // If the errorState has a general error, show the error alert
            if (errorData.general) 
                setShowErrorAlert(true);

        } finally {
            setButtonLoader(false);
        }
    }

    // Get the error message from the errorState
    const errorMessage = errorMessages['note'];

    // Handles the closing of the note form
    const handleClose = () => {
        if (cancelSource) {
            cancelSource.cancel();
        }
        
        closeForm?.();
        setNoteText('');
        setShowErrorAlert(false);
        setErrorMessages({});
        setButtonLoader(false);

        if (showInModal) {
            closeModal();
        }
    }

    return (
        (loadStatus === 'loading' || loadStatus === 'idle') && placement === 'inline' ? (
            <div className='note-form-loader'>
                <div className={`loader ${showLoader ? '' : 'hide'}`}></div>
            </div>
        ) : loadStatus === 'loaded' ? (
            <form className='formset timeline-note-form' 
                  onSubmit={handleSubmit}>
                <div className='timeline-note-form-wrapper'>
                    <CustomQuillEditor
                        value={noteText}
                        ref={quillRef}
                        onChange={setNoteText}
                        initialFocus={true}
                        className={`note-form ${isFocused ? 'focused' : ''}`}
                        enableStyling='simple'
                        enableMentions={true}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                </div>
                {placement === 'modal' && errorMessage && (
                    <div className='error-message'>
                        {t(errorMessage, { defaultValue: errorMessage})}
                    </div>
                )}
                <div className='button-row'>
                    <div className='buttons-left'>
                        {placement === 'modal' ? (
                            noteId && (
                                <SecondaryButton
                                    onClick={handleDelete} 
                                    label="timeline.note.general.delete_note_label"
                                    size='extra-small'
                                    customClass='danger'
                                />
                            )
                        ) : (
                            errorMessage && (
                                <div className='error-message'>
                                    {t(errorMessage, { defaultValue: errorMessage})}
                                </div>
                            )
                        )}
                    </div>
                    <div className='buttons-right'>
                        <SecondaryButton 
                            onClick={(e: React.MouseEvent) => {e.stopPropagation(); handleClose()}} 
                            label="general.cancel"
                            size={placement === 'inline' ? 'extra-small' : 'small'}
                        />
                        <PrimaryButton
                            type="submit" 
                            label="general.save"
                            size={placement === 'inline' ? 'extra-small' : 'small'}
                            loading={buttonLoader}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        />
                    </div>
                </div>
            </form>
        ) : null
    );
};

export default TimelineNoteForm;